import { inject as service } from '@ember/service';
import { resolve } from 'rsvp';
import Base from 'ember-simple-auth/authenticators/base';
import { setAccessCookie, getAccessCookie } from 'cio/utils/auth-cookies';

import { TwoFactorAuthRequiredError } from 'cio/authenticators/login-2fa';
import ApiService from 'cio/services/api';
import { LoginSuccessResponse, LoginTwoFactorAuthRequiredResponse } from 'cio/authenticators/login-email-password';
import { LoginMeta } from 'cio/pods/login/controller';
import { IdentityProvider } from 'cio/models/sso-configuration';

export type ChooseUserEntryDataObject = {
  userID: string;
  userEmail: string;
  userExternalIdentityEmail: string;
  accountName: string;
  accountLogo: string;
  accountSSOProvider: IdentityProvider | '';
  accountSSOOIDCGoogleHostedDomain: string;
};

type LoginGoogleOneClickResponse = {
  choose_user_jwt_token: string;
  google_account_email: string;
  choose_user_entries: {
    user_id: string;
    user_external_identity_email: string;
    user_email: string;
    account_name: string;
    account_logo: string;
    account_sso_provider: IdentityProvider | '';
    account_sso_oidc_google_hosted_domain: string;
  }[];
};

export class GoogleOneClickMustChooseUserAccountError extends Error {
  usersData: ChooseUserEntryDataObject[];
  chooseUserJWTToken: string;
  googleAccountEmail: string;

  constructor(
    usersData: ChooseUserEntryDataObject[],
    chooseUserJWTToken: string,
    googleAccountEmail: string,
    ...params: any
  ) {
    super(...params);

    this.usersData = usersData;
    this.chooseUserJWTToken = chooseUserJWTToken;
    this.googleAccountEmail = googleAccountEmail;
  }
}

export default class LoginGoogleOneClickAuthenticator extends Base {
  @service api!: ApiService;

  restore(_data: unknown) {
    return resolve({
      accessToken: getAccessCookie() as string,
    });
  }

  authenticate({
    authorizationCode,
    redirectUri,
    meta,
  }: {
    authorizationCode: string;
    redirectUri: string;
    meta: LoginMeta;
  }) {
    return this.api
      .post('login_google_one_click', {
        data: { code: authorizationCode, redirect_uri: redirectUri, ga_client_id: meta.ga_client_id },
        ignoreErrors: true,
        pathScope: 'global',
      })
      .then((res: LoginSuccessResponse | LoginTwoFactorAuthRequiredResponse | LoginGoogleOneClickResponse) => {
        if ('choose_user_entries' in res) {
          // more than one user to display in account chooser
          // don't login, reject with info for user selection:
          throw new GoogleOneClickMustChooseUserAccountError(
            res.choose_user_entries.map((u) => ({
              userID: u.user_id,
              userExternalIdentityEmail: u.user_external_identity_email,
              userEmail: u.user_email,
              accountName: u.account_name,
              accountLogo: u.account_logo,
              accountSSOProvider: u.account_sso_provider,
              accountSSOOIDCGoogleHostedDomain: u.account_sso_oidc_google_hosted_domain,
            })),
            res.choose_user_jwt_token,
            res.google_account_email,
          );
        }

        if ('2fa_login_token' in res) {
          // one available user account found, requiring 2FA: reject with 2FA login token
          throw new TwoFactorAuthRequiredError(res['2fa_login_token']);
        }

        // one available user account found, not requiring 2FA
        // user access token is already returned - use it to initialize session
        setAccessCookie(res.access_token);

        return { accessToken: res.access_token };
      });
  }
}
